import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShoppingBag, ArrowLeft, MapPin, Clock, Package, Leaf, Recycle, Certificate } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import TransparencyCard from '@/components/custom/TransparencyCard';
import BlockchainPassport from '@/components/custom/BlockchainPassport';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [artisan, setArtisan] = useState(null);
  const [transparency, setTransparency] = useState(null);
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, transparencyRes, passportRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/products/${id}/transparency`),
          axios.get(`${API}/products/${id}/passport`)
        ]);
        
        setProduct(productRes.data);
        setTransparency(transparencyRes.data);
        setPassport(passportRes.data);

        // Fetch artisan details
        if (productRes.data.artisan_id) {
          const artisanRes = await axios.get(`${API}/artisans/${productRes.data.artisan_id}`);
          setArtisan(artisanRes.data);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAddToCart = async () => {
    await addToCart(product.id);
    toast.success('Added to cart', {
      description: `${product.name} has been added to your cart.`
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 py-12">
            <div className="aspect-square bg-muted rounded-xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-muted rounded w-1/2 animate-pulse" />
              <div className="h-32 bg-muted rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-6 lg:px-12 text-center py-24">
          <h1 className="text-2xl font-medium mb-4">Product not found</h1>
          <Link to="/products">
            <Button variant="outline">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 ? product.images : ['https://images.pexels.com/photos/32795036/pexels-photo-32795036.jpeg'];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20">
        {/* Back Link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>
        </div>

        {/* Product Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
          <div className="grid lg:grid-cols-2 gap-12" data-testid="product-detail">
            {/* Left: Image Gallery */}
            <div className="space-y-4">
              <div className="aspect-square rounded-xl overflow-hidden bg-secondary">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product Info */}
            <div className="lg:sticky lg:top-24 lg:self-start space-y-6">
              <div>
                <Badge variant="outline" className="mb-3">{product.category}</Badge>
                <h1 className="text-3xl md:text-4xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.name}
                </h1>
                {product.story_tag && (
                  <p className="text-muted-foreground italic">"{product.story_tag}"</p>
                )}
              </div>

              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-bold">${product.price?.toFixed(2)}</span>
                <span className="text-sm text-muted-foreground">SKU: {product.sku}</span>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-border">
                <div className="flex items-center gap-3">
                  <Package weight="duotone" className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Material</p>
                    <p className="font-medium">{product.material}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin weight="duotone" className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Origin</p>
                    <p className="font-medium">{product.material_origin}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock weight="duotone" className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Crafting Time</p>
                    <p className="font-medium">{product.time_spent_hours} hours</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Leaf weight="duotone" className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Technique</p>
                    <p className="font-medium">{product.craftsmanship_method}</p>
                  </div>
                </div>
              </div>

              {/* Add to Cart */}
              <Button 
                className="w-full h-14 rounded-full text-lg font-medium" 
                onClick={handleAddToCart}
                disabled={cartLoading}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag weight="bold" className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>

              {/* Artisan Link */}
              {artisan && (
                <Link to={`/artisans/${artisan.id}`} className="block p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors">
                  <div className="flex items-center gap-4">
                    <img
                      src={artisan.image_url || 'https://images.pexels.com/photos/32160894/pexels-photo-32160894.jpeg'}
                      alt={artisan.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm text-muted-foreground">Crafted by</p>
                      <p className="font-medium">{artisan.name}</p>
                      <p className="text-sm text-muted-foreground">{artisan.location}</p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mt-16">
            <Tabs defaultValue="transparency" className="w-full">
              <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 gap-8">
                <TabsTrigger value="transparency" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary pb-3 px-0">
                  Transparency Score
                </TabsTrigger>
                <TabsTrigger value="passport" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary pb-3 px-0">
                  Blockchain Passport
                </TabsTrigger>
                <TabsTrigger value="sustainability" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary pb-3 px-0">
                  Sustainability
                </TabsTrigger>
                <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary pb-3 px-0">
                  Description
                </TabsTrigger>
              </TabsList>

              <TabsContent value="transparency" className="mt-8">
                <div className="max-w-md">
                  <TransparencyCard score={transparency?.overall_score} breakdown={transparency} />
                </div>
              </TabsContent>

              <TabsContent value="passport" className="mt-8">
                <div className="max-w-md">
                  <BlockchainPassport passport={passport} />
                </div>
              </TabsContent>

              <TabsContent value="sustainability" className="mt-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl">
                  {product.sustainability && (
                    <>
                      <SustainabilityItem 
                        icon={<Recycle />} 
                        label="Carbon Footprint" 
                        value={product.sustainability.carbon_footprint} 
                      />
                      <SustainabilityItem 
                        icon={<Certificate />} 
                        label="Certifications" 
                        value={product.sustainability.certifications?.join(', ') || 'None'} 
                      />
                      <SustainabilityItem 
                        icon={<Package />} 
                        label="Packaging" 
                        value={product.sustainability.packaging_type} 
                      />
                      <SustainabilityItem 
                        icon={<Leaf />} 
                        label="Lifecycle Stage" 
                        value={product.sustainability.fashion_lifecycle_stage} 
                      />
                      <SustainabilityItem 
                        icon={<Recycle />} 
                        label="End of Life" 
                        value={product.sustainability.end_of_life_option} 
                      />
                      <SustainabilityItem 
                        icon={<Clock />} 
                        label="Durability" 
                        value={product.sustainability.durability_lifespan} 
                      />
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="description" className="mt-8">
                <div className="max-w-2xl">
                  <p className="text-muted-foreground leading-relaxed">{product.description}</p>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Color</p>
                      <p className="font-medium">{product.color}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Size</p>
                      <p className="font-medium">{product.size}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const SustainabilityItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 p-4 bg-secondary/50 rounded-xl">
    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
      {icon}
    </div>
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  </div>
);

export default ProductDetailPage;
